/**
 * «Адміністрування акаунта» → «Безпека» (ч.1074, рішення власника: «дати
 * максимум налаштувань, а користувачі самі вирішують»).
 *
 * - Двофакторний вхід: вимога для всіх (діє і на власника) або для окремих
 *   користувачів; хто ще без двофакторного входу — нагадування.
 * - Довірені мережі (IP): для всіх, КРІМ власника, або для окремих
 *   користувачів; мережі спільні або особисті для користувача.
 * - Журнал заблокованих спроб.
 *
 * Бачать власник і адміністратор; змінює лише власник (бекенд перевіряє сам).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  tenantSecurity,
  type BlockedAttempt,
  type MemberSecurity,
  type TenantSecurity,
  type TrustedNetwork,
} from '@/api/tenantSecurity';
import { useConfirm } from '@/components/ConfirmProvider';
import { useToast } from '@/components/ToastProvider';
import { StepUpForm } from '@/components/security/TwoFactorGate';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { tenantRoleLabel } from '@/auth/roleLabels';
import { extractApiError } from '@/utils/extractApiError';

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: 'активний', INVITED: 'запрошено', SUSPENDED: 'призупинено',
};
const REASON_LABEL: Record<string, string> = {
  TWO_FACTOR_REQUIRED: 'без двофакторного входу',
  IP_NOT_ALLOWED: 'недовірена мережа',
};

const muted = { color: 'var(--text-secondary)', fontSize: '0.85rem' } as const;
const warn = {
  background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)',
  border: '1px solid var(--badge-amber-bd)', borderRadius: 'var(--radius-sm)',
  padding: '0.5rem 0.75rem', fontSize: '0.85rem', margin: '0.5rem 0',
} as const;

export default function SecuritySection() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const q = useQuery({ queryKey: ['tenant-security'], queryFn: tenantSecurity.get });
  const [stepUp, setStepUp] = useState(false);
  const [net, setNet] = useState({ network: '', label: '', user_id: '' });

  const fail = (e: unknown) => toast.show({ type: 'error', message: extractApiError(e) });
  const apply = (d: TenantSecurity) => qc.setQueryData(['tenant-security'], d);
  const setTenant = useMutation({
    mutationFn: tenantSecurity.set,
    onSuccess: (d) => { apply(d); toast.show({ type: 'success', message: 'збережено' }); },
    onError: fail,
  });
  const setMember = useMutation({
    mutationFn: (v: { id: string; body: { require_two_factor?: boolean; ip_restricted?: boolean } }) =>
      tenantSecurity.setMember(v.id, v.body),
    onSuccess: apply,
    onError: fail,
  });
  const addNet = useMutation({
    mutationFn: () => tenantSecurity.addNetwork({
      network: net.network.trim(), label: net.label.trim() || null,
      user_id: net.user_id || null,
    }),
    onSuccess: (d) => {
      apply(d);
      setNet({ network: '', label: '', user_id: '' });
      // ч.1075: кілька адрес за раз — кажемо, що додано і що вже було.
      toast.show({ type: 'success', message: `Додано: ${d.added.join(', ')}`
        + (d.already.length ? `. Уже були в списку: ${d.already.join(', ')}` : '') });
    },
    onError: fail,
  });
  const delNet = useMutation({ mutationFn: tenantSecurity.deleteNetwork, onSuccess: apply, onError: fail });
  const remind = useMutation({
    mutationFn: tenantSecurity.remind2fa,
    onSuccess: (r) => toast.show({ type: 'success',
      message: `Нагадування надіслано: ${r.notified}` }),
    onError: fail,
  });

  if (q.isLoading) return <p>Завантаження…</p>;
  if (q.isError || !q.data) return <div className="error">{extractApiError(q.error)}</div>;
  const s = q.data;
  const edit = s.can_edit;
  const busy = setTenant.isPending || setMember.isPending;
  const others = s.members.filter((m) => m.tenant_role !== 'OWNER');
  const without2fa = s.members.filter((m) => m.status === 'ACTIVE' && !m.has_two_factor);
  const restricted = s.members.filter((m) => m.networks_restricted);
  const noNetworksFor = restricted.filter((m) =>
    !s.networks.some((n) => n.user_id === null || n.user_id === m.user_id));

  async function toggle2fa(on: boolean) {
    if (on) {
      if (!s.me.has_two_factor) {
        toast.show({ type: 'warning', durationMs: 8000,
          message: 'Спершу увімкніть двофакторний вхід собі: «Обліковий запис» → «Облікові дані». Вимога діє і на власника.' });
        return;
      }
      if (!s.me.session_verified) { setStepUp(true); return; }
      const n = without2fa.length;
      if (n > 0 && !(await confirm({
        title: 'Вимагати двофакторний вхід?',
        message: `${n} корист. ще без двофакторного входу: вони зможуть увійти, але до даних `
          + 'акаунта не потраплять, доки не налаштують його (система запропонує це зробити).',
        okLabel: 'вимагати', cancelLabel: 'скасувати',
      }))) return;
    }
    setTenant.mutate({ require_two_factor: on, ip_restrict_all: s.ip_restrict_all });
  }

  async function toggleIpAll(on: boolean) {
    if (on && s.networks.filter((n) => n.user_id === null).length === 0 && !(await confirm({
      title: 'Обмежити всіх довіреними мережами?',
      message: 'Спільних мереж у списку ще немає: користувачі без особистих мереж не зможуть '
        + 'працювати в акаунті. Власника обмеження не стосується.',
      okLabel: 'обмежити', cancelLabel: 'скасувати',
    }))) return;
    setTenant.mutate({ require_two_factor: s.require_two_factor, ip_restrict_all: on });
  }

  const memberCols: ColumnDef<MemberSecurity>[] = [
    { key: 'email', header: 'Користувач', sortValue: (m) => m.email,
      cell: (m) => (<><div>{m.email}</div>{m.full_name && m.full_name !== m.email
        && <div style={muted}>{m.full_name}</div>}</>) },
    { key: 'role', header: 'Роль', cell: (m) => tenantRoleLabel(m.tenant_role),
      sortValue: (m) => m.tenant_role },
    { key: 'status', header: 'Статус', cell: (m) => STATUS_LABEL[m.status] ?? m.status },
    { key: 'has2fa', header: 'Двофакторний вхід', align: 'center',
      cell: (m) => (m.has_two_factor ? 'налаштовано'
        : <span style={{ color: 'var(--badge-amber-fg)' }}>ні</span>) },
    { key: 'req2fa', header: 'Вимагати 2FA', align: 'center', cell: (m) => {
      if (s.require_two_factor) return <span style={muted}>для всіх</span>;
      if (m.tenant_role === 'OWNER') return <span style={muted}>—</span>;
      return (
        <input type="checkbox" style={{ width: 'auto' }} checked={m.require_two_factor}
          aria-label={`Вимагати 2FA: ${m.email}`} disabled={!edit || busy}
          onChange={(e) => setMember.mutate({ id: m.membership_id,
            body: { require_two_factor: e.target.checked } })} />
      );
    } },
    { key: 'ip', header: 'Лише довірені мережі', align: 'center', cell: (m) => {
      if (m.tenant_role === 'OWNER') return <span style={muted}>без обмежень</span>;
      if (s.ip_restrict_all) return <span style={muted}>для всіх</span>;
      return (
        <input type="checkbox" style={{ width: 'auto' }} checked={m.ip_restricted}
          aria-label={`Лише довірені мережі: ${m.email}`} disabled={!edit || busy}
          onChange={(e) => setMember.mutate({ id: m.membership_id,
            body: { ip_restricted: e.target.checked } })} />
      );
    } },
    { key: 'own', header: 'Особисті мережі', align: 'center',
      cell: (m) => (m.tenant_role === 'OWNER' ? '' : m.personal_networks || '—'),
      sortValue: (m) => m.personal_networks },
  ];

  const netCols: ColumnDef<TrustedNetwork>[] = [
    { key: 'network', header: 'Мережа', cell: (n) => <code>{n.network}</code>,
      sortValue: (n) => n.network },
    { key: 'label', header: 'Назва', cell: (n) => n.label ?? '' },
    { key: 'who', header: 'Для кого',
      cell: (n) => n.user_email ?? <span style={muted}>усі користувачі з обмеженням</span> },
    { key: 'actions', header: '', align: 'right', cell: (n) => (edit ? (
      <button type="button" className="danger" disabled={delNet.isPending}
        onClick={() => delNet.mutate(n.id)}>прибрати</button>
    ) : null) },
  ];

  const logCols: ColumnDef<BlockedAttempt>[] = [
    { key: 'at', header: 'Час', sortValue: (b) => b.created_at,
      cell: (b) => new Date(b.created_at).toLocaleString('uk-UA') },
    { key: 'user', header: 'Користувач', cell: (b) => b.user_email },
    { key: 'reason', header: 'Причина', cell: (b) => REASON_LABEL[b.reason] ?? b.reason },
    { key: 'ip', header: 'Адреса', cell: (b) => b.ip ?? '—' },
  ];

  return (
    <div>
      {!edit && (
        <div style={warn}>
          Змінювати налаштування безпеки може лише власник акаунта — тут ви їх
          бачите.
        </div>
      )}

      <div className="card" style={{ marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>Двофакторний вхід</h3>
        <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={s.require_two_factor}
            disabled={!edit || busy}
            onChange={(e) => { void toggle2fa(e.target.checked); }} />
          <b>Вимагати двофакторний вхід від усіх користувачів акаунта — і від власника</b>
        </label>
        <p style={muted}>
          Користувач без двофакторного входу зможе увійти в систему, але до даних
          акаунта не потрапить: система запропонує налаштувати двофакторний вхід
          (Google Authenticator або код у Telegram) або підтвердити вхід кодом.
          Вимогу для окремих людей — у таблиці «Користувачі» нижче.
        </p>
        {edit && !s.me.has_two_factor && (
          <div style={warn}>
            Щоб увімкнути вимогу, спершу увімкніть двофакторний вхід собі:{' '}
            <Link to="/account">«Обліковий запис» → «Облікові дані»</Link>.
          </div>
        )}
        {stepUp && (
          <div style={{ ...warn, color: 'var(--text)' }}>
            <p style={{ marginTop: 0 }}>
              Підтвердіть цей вхід кодом двофакторного входу — вимога діятиме і
              для вас, тож без підтвердження вона одразу заблокувала б вашу сесію.
            </p>
            <StepUpForm onDone={() => {
              setStepUp(false);
              setTenant.mutate({ require_two_factor: true, ip_restrict_all: s.ip_restrict_all });
            }} />
          </div>
        )}
        {without2fa.length > 0 && (
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={muted}>
              Без двофакторного входу: {without2fa.map((m) => m.email).join(', ')}
            </span>
            {edit && (
              <button type="button" disabled={remind.isPending}
                onClick={() => remind.mutate()}>надіслати нагадування</button>
            )}
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>Довірені мережі (IP)</h3>
        <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={s.ip_restrict_all}
            disabled={!edit || busy}
            onChange={(e) => { void toggleIpAll(e.target.checked); }} />
          <b>Працювати лише з довірених мереж — усі користувачі, крім власника</b>
        </label>
        <p style={muted}>
          Користувач з обмеженням працює в акаунті лише з адрес зі списку (спільних
          або його особистих). Перевірка — на кожній дії, не лише при вході.
          Власника мережами не обмежуємо. Ваша поточна адреса:{' '}
          <b>{s.me.client_ip ?? 'невідома'}</b>.
        </p>
        {noNetworksFor.length > 0 && (
          <div style={warn}>
            Для користувачів з обмеженням немає жодної мережі — зараз вони не можуть
            працювати в акаунті: {noNetworksFor.map((m) => m.email).join(', ')}.
          </div>
        )}
        {edit && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 1.2fr) minmax(120px, 1fr) minmax(160px, 1.2fr) auto',
            gap: '0.5rem', alignItems: 'end', margin: '0.5rem 0 0.75rem' }}>
            <div className="field" style={{ margin: 0 }}>
              <label>Адреси або мережі (через кому)</label>
              <input value={net.network} placeholder="91.200.10.15, 91.200.11.0/24"
                onChange={(e) => setNet({ ...net, network: e.target.value })} />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label>Назва</label>
              <input value={net.label} placeholder="Офіс" maxLength={200}
                onChange={(e) => setNet({ ...net, label: e.target.value })} />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label>Для кого</label>
              <select value={net.user_id} aria-label="Для кого"
                onChange={(e) => setNet({ ...net, user_id: e.target.value })}>
                <option value="">усі користувачі з обмеженням</option>
                {others.map((m) => (
                  <option key={m.user_id} value={m.user_id}>лише {m.email}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button type="button" className="primary"
                disabled={!net.network.trim() || addNet.isPending}
                onClick={() => addNet.mutate()}>додати</button>
              {s.me.client_ip && (
                <button type="button" title="Підставити вашу поточну адресу"
                  onClick={() => setNet({ ...net, network: net.network.trim()
                    ? `${net.network.trim()}, ${s.me.client_ip!}` : s.me.client_ip! })}>
                  моя адреса
                </button>
              )}
            </div>
          </div>
        )}
        <DataTable rows={s.networks} columns={netCols} rowKey={(n) => n.id}
          storageKey="tenant-security-networks"
          emptyText="Довірених мереж ще немає." />
      </div>

      <div className="card" style={{ marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>Користувачі</h3>
        <DataTable rows={s.members} columns={memberCols} rowKey={(m) => m.membership_id}
          storageKey="tenant-security-members" emptyText="Користувачів немає." />
      </div>

      <div className="card" style={{ marginTop: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>Журнал заблокованих спроб</h3>
        <p style={muted}>
          Останні 100 відмов: хто, коли, з якої адреси і чому не потрапив до
          даних акаунта (повтори тієї самої причини з тієї самої адреси —
          не частіше разу на 10 хвилин).
        </p>
        <DataTable rows={s.blocked} columns={logCols}
          rowKey={(b) => `${b.created_at}|${b.user_email}|${b.reason}|${b.ip}`}
          storageKey="tenant-security-blocked" initialSortKey="at" initialSortDir="desc"
          emptyText="Заблокованих спроб не було." />
      </div>
    </div>
  );
}
